import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Star } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage, mediaUrl } from "../../api";

const Pill = ({ status }) => (
  <span className={`chip !text-[11px] uppercase tracking-wide ${status === "published" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"}`} data-testid="case-status">
    {status}
  </span>
);

export default function AdminCaseStudies() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/case-studies").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const toggle = async (c) => {
    const status = c.status === "published" ? "draft" : "published";
    try {
      const { data } = await api.patch(`/admin/case-studies/${c.id}/status`, { status });
      setItems((l) => l.map((x) => (x.id === c.id ? data : x)));
      toast.success(status === "published" ? "Case study published" : "Moved to drafts");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete “${c.company}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/case-studies/${c.id}`);
      setItems((l) => l.filter((x) => x.id !== c.id));
      toast.success("Case study deleted");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const shown = (items || []).filter((c) => filter === "all" || c.status === filter);
  const count = (f) => (f === "all" ? items?.length : items?.filter((c) => c.status === f).length) || 0;

  return (
    <div data-testid="admin-cases-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Customers</span>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">Case studies</h1>
          <p className="text-sm text-[var(--fr-muted)] mt-2">Published stories appear on /case-studies and the first three on the Home page (featured first).</p>
        </div>
        <Link to="/admin/case-studies/new" className="btn btn-primary" data-testid="admin-new-case">
          <Plus size={16} className="mr-1" /> New case study
        </Link>
      </div>

      <div className="mt-8 flex gap-2" data-testid="admin-cases-filters">
        {["all", "published", "draft"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`h-9 px-4 rounded-full text-sm font-medium border capitalize transition-colors ${
              filter === f ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
            }`}
            data-testid={`admin-cases-filter-${f}`}
          >
            {f} <span className="opacity-60">{count(f)}</span>
          </button>
        ))}
      </div>

      <div className="card mt-6 overflow-hidden">
        {items === null ? (
          <div className="p-10 text-center text-sm text-[var(--fr-muted)]">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-cases-empty">
            <h3 className="font-display font-bold text-lg">No case studies here</h3>
            <p className="text-sm text-[var(--fr-muted)] mt-1">Create your first customer story.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--fr-border)]" data-testid="admin-cases-list">
            {shown.map((c) => (
              <li key={c.id} className="p-4 md:px-6 flex items-center gap-4 hover:bg-[var(--fr-bg)] transition-colors" data-testid={`admin-case-row-${c.slug}`}>
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-[var(--fr-bg-alt)] flex-shrink-0 hidden sm:flex items-center justify-center">
                  {c.cover ? <img src={mediaUrl(c.cover)} alt="" loading="lazy" className="w-full h-full object-cover" /> : c.logo ? <img src={mediaUrl(c.logo)} alt="" loading="lazy" className="max-h-8 max-w-[64px] object-contain" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill status={c.status} />
                    {c.featured && (
                      <span className="chip !text-[11px] uppercase tracking-wide !bg-[var(--fr-primary-tint)] !text-[var(--fr-primary-dark)] !border-transparent inline-flex items-center gap-1" data-testid="case-featured-badge">
                        <Star size={10} /> Featured
                      </span>
                    )}
                    <span className="text-xs text-[var(--fr-muted)]">
                      {c.sector}
                      {c.location ? ` · ${c.location}` : ""}
                    </span>
                  </div>
                  <Link to={`/admin/case-studies/${c.id}`} className="block font-display font-bold mt-1 truncate hover:text-[var(--fr-primary-dark)]" data-testid="admin-case-company">
                    {c.company}
                  </Link>
                  <div className="text-xs text-[var(--fr-muted)] mt-0.5 truncate">{c.title}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {c.status === "published" && (
                    <a href={`/case-studies/${c.slug}`} target="_blank" rel="noreferrer" className="icon-btn" title="View on site" data-testid="admin-case-view">
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button type="button" onClick={() => toggle(c)} className="icon-btn" title={c.status === "published" ? "Unpublish" : "Publish"} data-testid="admin-case-toggle">
                    {c.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <Link to={`/admin/case-studies/${c.id}`} className="icon-btn" title="Edit" data-testid="admin-case-edit">
                    <Pencil size={14} />
                  </Link>
                  <button type="button" onClick={() => remove(c)} className="icon-btn danger" title="Delete" data-testid="admin-case-delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
